/**
 * The section feedback pipeline: AI section feedback generated without the
 * section leader asking, then turned into tailored puzzles, with an admin
 * view of where each section week stands.
 */
import {z} from 'zod';

import {PrepPlaylistWeekStatsSchema} from './prepPlaylist';
import {TailoredPuzzleJobStatusSchema} from './tailoredPuzzle';

/** The course feature that generates section feedback without the SL asking. */
export const EAGER_SECTION_FEEDBACK_FEATURE = 'eager-section-feedback';

/** `triggeredByUserId` on feedback the pipeline generated, not a person. */
export const PIPELINE_FEEDBACK_TRIGGER_UID = 'system:pipeline';

/**
 * The survey questions feedback reads answers to: the long-response
 * questions of the webapp's `POST_SECTION_SURVEY_QUESTIONS`.
 */
export const SL_FEEDBACK_SURVEY_QUESTIONS: ReadonlyArray<{
  id: string;
  questionText: string;
}> = [
  {id: 'like', questionText: 'What did you like about your last section?'},
  {
    id: 'wish',
    questionText:
      'What do you wish could have been different about your last section?',
  },
];

/** Firestore subcollection (under `course/{courseId}`) of feedback jobs. */
export const SECTION_FEEDBACK_JOBS_COLLECTION = 'sectionFeedbackJobs';

/**
 * Where generating one section week's feedback stands: `queued` until a
 * worker takes it, then `generating`, and `done`, `skipped` (opted out, or no
 * transcript) or `failed`.
 */
export const SectionFeedbackJobStatusSchema = z.enum([
  'queued',
  'generating',
  'done',
  'skipped',
  'failed',
]);

/** A feedback job's status. */
export type SectionFeedbackJobStatus = z.infer<
  typeof SectionFeedbackJobStatusSchema
>;

/** One section week's feedback job. Server-written; admins read it. */
export const SectionFeedbackJobSchema = z.object({
  /** Where it stands. */
  status: SectionFeedbackJobStatusSchema,
  /** The section. */
  sectionId: z.string(),
  /** The section week. */
  sectionWeek: z.number().int().positive(),
  /** Who queued it: the eager schedule, or an admin's backfill. */
  source: z.enum(['eager', 'backfill']),
  /** When it last changed status, in epoch ms. */
  updatedAtMs: z.number(),
  /** Why it was skipped or failed. Omitted: none. */
  reason: z.string().optional(),
});

/** One section week's feedback job. */
export type SectionFeedbackJob = z.infer<typeof SectionFeedbackJobSchema>;

/**
 * Path of a section week's feedback job.
 *
 * @param args.courseId - The course.
 * @param args.sectionId - The section.
 * @param args.sectionWeek - The section week.
 * @return `course/{courseId}/sectionFeedbackJobs/{sectionId}-w{week}`.
 * @throws Never.
 */
export function sectionFeedbackJobDocPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: string;
  sectionId: string;
  sectionWeek: number;
}): string {
  return `course/${courseId}/${SECTION_FEEDBACK_JOBS_COLLECTION}/${sectionId}-w${String(sectionWeek)}`;
}

/** The admin callable that reports a section week's pipeline per section. */
export const GET_SECTION_PIPELINE_FUNCTION = 'getsectionpipeline';

/** Request for {@link GET_SECTION_PIPELINE_FUNCTION}. */
export interface GetSectionPipelineRequest {
  /** The course. */
  courseId: string;
  /** The section week. Omitted: the course's current section week. */
  sectionWeek?: number;
}

/** One section's row in the pipeline report. */
export const SectionPipelineRowSchema = z.object({
  /** The section. */
  sectionId: z.string(),
  /** Its number, for display. Omitted: none set. */
  sectionNumber: z.number().optional(),
  /** Who taught this week. Omitted: nobody assigned. */
  slUid: z.string().optional(),
  /** The section's name. Omitted: none set. */
  sectionName: z.string().optional(),
  /** Whether the teaching SL opted out of AI feedback. */
  isOptedOut: z.boolean(),
  /** Whether the week's recording has a transcript yet. */
  hasTranscript: z.boolean(),
  /** The latest feedback document's state; `none` when there is none. */
  feedback: z.enum(['none', 'succeeded', 'claiming', 'failed']),
  /** Whether the feedback names a moment, which a puzzle needs. */
  hasMoment: z.boolean(),
  /** The feedback job, when the pipeline queued one. */
  feedbackJob: z
    .object({
      status: SectionFeedbackJobStatusSchema,
      reason: z.string().optional(),
      updatedAtMs: z.number(),
    })
    .optional(),
  /** The tailored puzzle, when one is saved. */
  puzzle: z
    .object({id: z.string(), title: z.string(), isPublished: z.boolean()})
    .optional(),
  /** The puzzle job, when one ran or is queued. */
  puzzleJob: z
    .object({
      status: TailoredPuzzleJobStatusSchema,
      reason: z.string().optional(),
      startedAtMs: z.number(),
      /** A `generating` job that outlived any real run. */
      isStale: z.boolean(),
    })
    .optional(),
});

/** One section's row in the pipeline report. */
export type SectionPipelineRow = z.infer<typeof SectionPipelineRowSchema>;

/** Response from {@link GET_SECTION_PIPELINE_FUNCTION}. */
export const GetSectionPipelineResponseSchema = z.object({
  /** The week reported. */
  sectionWeek: z.number(),
  /** The course's current section week. Omitted: the course has no timing. */
  currentSectionWeek: z.number().optional(),
  /** How many section weeks the course has. Omitted: unknown. */
  nWeeks: z.number().optional(),
  /** Whether feedback is generated without the SL asking. */
  isEagerFeedbackOn: z.boolean(),
  /** Whether feedback moments become tailored puzzles. */
  isTailoredPuzzlesOn: z.boolean(),
  /** The week whose prep playlists SLs see. Omitted: off. */
  shownPrepPlaylistWeek: z.number().int().positive().optional(),
  /** Counts for every week that has built prep playlists, by week. */
  prepPlaylistWeeks: z.array(PrepPlaylistWeekStatsSchema),
  /** One row per active, non-test section. */
  rows: z.array(SectionPipelineRowSchema),
});

/** Response from {@link GET_SECTION_PIPELINE_FUNCTION}. */
export type GetSectionPipelineResponse = z.infer<
  typeof GetSectionPipelineResponseSchema
>;

/** The admin callable that queues feedback or puzzles for many sections. */
export const QUEUE_SECTION_PIPELINE_FUNCTION = 'queuesectionpipeline';

/** Request for {@link QUEUE_SECTION_PIPELINE_FUNCTION}. */
export interface QueueSectionPipelineRequest {
  /** The course. */
  courseId: string;
  /** The section week. */
  sectionWeek: number;
  /** What to generate. */
  kind: 'feedback' | 'puzzle';
  /** The sections to generate it for. */
  sectionIds: string[];
}

/** Response from {@link QUEUE_SECTION_PIPELINE_FUNCTION}. */
export const QueueSectionPipelineResponseSchema = z.object({
  /** Sections whose job was queued. */
  queued: z.array(z.string()),
  /** Sections left alone, and why. */
  skipped: z.array(z.object({sectionId: z.string(), reason: z.string()})),
});

/** Response from {@link QUEUE_SECTION_PIPELINE_FUNCTION}. */
export type QueueSectionPipelineResponse = z.infer<
  typeof QueueSectionPipelineResponseSchema
>;
